import React, { useMemo } from 'react';
import { downloadTextAsFile } from 'src/shared/helpers/downloadAsFile';
import { CommitProgressBar } from './CommitProgressBar';
import { CommitSummaryBanner } from './CommitSummaryBanner';
import { FailedRowsTable } from './FailedRowsTable';
import { serializeFailedRowsCsv } from '../../helpers/serializeFailedRowsCsv';
import type { CommitSummary, RowCommitState } from '../../commit';
import type { PreviewColumn, PreviewRowCells, PreviewSiteManifestResponse } from '../../state/api/organisationEngineSlice';

type CommitProgressProps = {
    rowStates: Record<number, RowCommitState>;
    summary: CommitSummary | null;
    /** Pre-commit preview — supplies the per-row cells used by the failure table + download. */
    preview: PreviewSiteManifestResponse;
    /** Column registry served from the schema endpoint (org-independent, cached). */
    columns: PreviewColumn[];
    isCommitting: boolean;
    /** Opens the re-upload drawer; passed down so the failure banner can offer the same affordance. */
    onReupload: () => void;
    /** Used as part of the failed-rows download filename so Ops can tell which org/manifest it came from. */
    bcId: string;
};

// Renders the commit lifecycle without re-listing every row: a progress bar
// reports headway while the orchestration runs, the summary banner reports
// outcomes (counts) when it settles, and only the *failed* rows get listed
// — that's the actionable subset Ops needs to inspect and fix. Successful
// and skipped rows live as counts in the summary.
export const CommitProgress = ({ rowStates, summary, preview, columns, isCommitting, onReupload, bcId }: CommitProgressProps) => {
    const failedRows = Object.values(rowStates)
        .filter(s => s.overallStatus === 'failed')
        .sort((a, b) => a.rowNumber - b.rowNumber);

    // Index preview rows so the failure UI + download can pull the cells the
    // engine parsed (no client-side CSV parse).
    const cellsByRowNumber = useMemo(() => new Map<number, PreviewRowCells>(preview.rows.map(row => [row.row, row.cells])), [preview.rows]);

    const onDownloadFailed = async () => {
        const csv = serializeFailedRowsCsv(
            failedRows.map(r => ({ rowNumber: r.rowNumber, cells: cellsByRowNumber.get(r.rowNumber) })),
            columns
        );
        await downloadTextAsFile(csv, `site_manifest_${bcId}_failed.csv`, { type: 'text/csv' });
    };

    return (
        <div data-testid='commit-progress' className='flex flex-col gap-3'>
            <h2 className='text-base font-semibold'>{isCommitting ? 'Onboarding sites…' : 'Onboarding complete'}</h2>
            {isCommitting && <CommitProgressBar summaryHint={preview.summary} rowStates={rowStates} />}
            {summary && (
                <CommitSummaryBanner
                    summary={summary}
                    isCommitting={isCommitting}
                    onReupload={onReupload}
                    onDownloadFailed={onDownloadFailed}
                />
            )}
            {failedRows.length > 0 && <FailedRowsTable rows={failedRows} columns={columns} cellsByRowNumber={cellsByRowNumber} />}
        </div>
    );
};
